/**
 * CollabRequestComposer – modal for å sende en samarbeid-forespørsel
 * basert på en valgt slot fra Finn-skapere-resultatet.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const COLLAB_TYPES = ["sfs", "mass", "l4l", "story_swap"];
const PLATFORMS = ["onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other"];

export default function CollabRequestComposer({ creator, slot, accounts = [], defaultType, defaultPlatform, onClose, onSent }) {
  const { t } = useTranslation();
  const [type, setType] = useState(defaultType || (slot?.types?.[0]) || "sfs");
  const [selectedPlatforms, setSelectedPlatforms] = useState(
    defaultPlatform ? [defaultPlatform] : (slot?.platforms || []).slice(0, 3)
  );
  const [message, setMessage] = useState("");
  const [packetId, setPacketId] = useState("");
  const [packets, setPackets] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/collab/promo-packets").then((r) => setPackets(r.data?.results || [])).catch(() => {});
  }, []);

  const togglePlatform = (p) => {
    setSelectedPlatforms((cur) => cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]);
  };

  const send = async () => {
    if (selectedPlatforms.length === 0) {
      toast.error(t("collab.err_pick_platform"));
      return;
    }
    setSending(true);
    try {
      await api.post("/collab/requests", {
        target_username: creator.username,
        type,
        platforms: selectedPlatforms,
        scheduled_date: slot.date,
        message: message.trim(),
        promo_packet_id: packetId || null,
      });
      toast.success(t("collab.request_sent"));
      onSent?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.send_err"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="request-composer">
      <div className="max-w-lg w-full panel-tight p-6 relative my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">
          {t("collab.send_request_to", { username: creator.creator_display_name || creator.username })}
        </h3>
        <p className="text-sm text-[var(--cream-soft)] mb-5">
          {t("collab.scheduled_for")} <span className="text-[var(--cream)] font-medium">{slot.date}</span>
        </p>

        <div className="space-y-4">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.type_label")}
            </label>
            <div className="flex flex-wrap gap-1">
              {COLLAB_TYPES.map((tp) => (
                <button key={tp} onClick={() => setType(tp)}
                  className={`px-3 py-1.5 font-meta text-[11px] border ${type === tp ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                  data-testid={`composer-type-${tp}`}>
                  {tp.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.platforms_label")}
            </label>
            <div className="flex flex-wrap gap-1">
              {PLATFORMS.map((p) => (
                <button key={p} onClick={() => togglePlatform(p)}
                  className={`px-3 py-1.5 font-meta text-[11px] border ${selectedPlatforms.includes(p) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                  data-testid={`composer-platform-${p}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {packets.length > 0 && (
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                {t("collab.promo_packet_label")}
              </label>
              <select value={packetId} onChange={(e) => setPacketId(e.target.value)}
                className="input-editorial w-full" data-testid="composer-packet">
                <option value="">{t("collab.no_packet")}</option>
                {packets.map((p) => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.message_label")}
            </label>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)}
              rows={4} maxLength={500}
              placeholder={t("collab.message_placeholder")}
              className="input-editorial w-full font-manrope text-sm"
              data-testid="composer-message" />
          </div>
        </div>

        <button onClick={send} disabled={sending}
          className="btn-magenta w-full mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="composer-send">
          <Send className="h-4 w-4" /> {sending ? t("collab.sending") : t("collab.send_request")}
        </button>
      </div>
    </div>
  );
}
